import { useEffect, useMemo } from 'react';

import { useForm } from '@givebacks/formy';
import { Box, Collapse, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { TransformedValues, UseFormReturnType, hasLength, isEmail } from '@mantine/form';
import { randomId } from '@mantine/hooks';
import { DateTime } from 'luxon';

import { MultiSelect, Select } from '../../../../Combobox';
import { DatePickerInput } from '../../../../Dates/DatePickerInput/DatePickerInput';
import { Checkbox, CurrencyInput, NumberInput } from '../../../../Inputs';
import { AdditionalFieldOption, AdditionalFieldOptionWithKey } from './AdditionalFields.types';
import { CustomInputProps } from './customInputProps.types';
import { AdditionalMailingAddressField } from './fields/MailingAddress';

const arraysEqual = (arrayA: string[], arrayB: string[]) =>
  arrayA.length === arrayB.length && arrayA.every((value, index) => value === arrayB[index]);

const InputsMap = {
  number: NumberInput,
  dropdown: Select,
  textfield: TextInput,
  textarea: Textarea,
  dollar: CurrencyInput,
  email: TextInput,
  phonenumber: TextInput,
  checkbox: Checkbox,
  boolean: Checkbox,
  date: DatePickerInput,
  address: AdditionalMailingAddressField,
  multiselect: MultiSelect,
} as const;

type AdditionalFieldWithValue = AdditionalFieldOptionWithKey & { value: string | string[] };

type Transformed = TransformedValues<UseFormReturnType<AdditionalFormFieldsForm>>;

type transformedValues = (values: Transformed) => {
  options: FormattedValues;
};

type AdditionalFormFieldsForm = {
  options: AdditionalFieldWithValue[];
};

type FormattedValues = {
  asArray: AdditionalFieldOption[];
  asObject: Record<string, string | string[]>;
  asObjectId: Record<string, string | string[]>;
};

type UseAdditionalFieldsForm = UseFormReturnType<AdditionalFormFieldsForm, transformedValues>;

type AdditionalFieldsFormsProps = {
  options: AdditionalFieldOption[];
  values?: Record<string, string | string[]>;
  onChange?: (options: AdditionalFieldOption[], formattedValues: FormattedValues) => void;
  /**
   * When using useFormValidator, optionally pass the formId to be used
   */
  formId?: string;

  /**
   * Exclude certain options from the list of available options
   */
  excludedOptions?: AdditionalFieldOption['type'][];

  /**
   * Select options only specific options to be shown from the provided options.
   * Will return after excluding the excludedOptions
   *
   */
  selectOptions?: AdditionalFieldOption['type'][];

  /**
   * Title to be displayed on the form
   */
  title?: React.ReactNode;
  /**
   * Props to be passed to the title component, if title is a string
   */
  titleProps?: Partial<React.ComponentProps<typeof Text>>;
};

type FieldProps = {
  inputProps: CustomInputProps;
} & AdditionalFieldWithValue;

const SectionTitle = ({
  title,
  titleProps,
}: Pick<AdditionalFieldsFormsProps, 'title' | 'titleProps'>) => {
  if (!title) return null;
  if (typeof title === 'string')
    return (
      <Text fz={16} fw="600" c="gray.8" {...titleProps}>
        {title}
      </Text>
    );
  return <Box>{title}</Box>;
};

const formatValues = (values: AdditionalFieldWithValue[]) => {
  const asArray = values.map(({ key, ...rest }) => rest);
  const asObject = values.reduce(
    (acc, { name, value, type }) => {
      let _value = value;

      if (type === 'multiselect' && !value) {
        _value = [];
      }

      acc[name] = _value;
      return acc;
    },
    {} as Record<string, string | string[]>
  );
  /**
   * Returns the objects using the id as the object key
   */
  const asObjectId = values.reduce(
    (acc, { id, value }) => {
      if (id) {
        acc[id] = value;
      }
      return acc;
    },
    {} as Record<string, string | string[]>
  );
  return { asArray, asObject, asObjectId };
};

const useGetSharedProps = ({
  type,
  name,
  options,
  ...props
}: AdditionalFieldWithValue & { inputProps: CustomInputProps }) => {
  const placeholder = useMemo(() => {
    if (type === 'dropdown') return `Select ${name}`;
    if (type === 'multiselect') return `Select ${name}`;
    return `Enter ${name}`;
  }, [name, type]);

  const label = useMemo(() => name, [name]);

  const data = useMemo(() => {
    // removes duplicate options for the selection list
    return Array.from(new Set(options));
  }, [options]);

  const id = useMemo(() => {
    if (type == 'address') {
      return 'address';
    }
    return undefined;
  }, [type]);

  const autosize = useMemo(() => {
    if (type == 'textarea') {
      return 'autosize';
    }
    return undefined;
  }, [type]);

  const _type = useMemo(() => {
    if (type == 'date') {
      return 'default';
    }
  }, [type]);

  const value = useMemo(() => {
    if (type == 'date') {
      if (!props.inputProps.value) return null;
      return props.inputProps.value;
    }
    return props.inputProps.value || undefined;
  }, [props, type]);

  return { placeholder, label, data, id, autosize, type: _type, value };
};

const Field = ({ inputProps, ...props }: FieldProps) => {
  const sharedProps = useGetSharedProps({ ...props, inputProps });

  const Component = InputsMap[props.type];

  if (!Component) return null;

  return <Component {...props} {...inputProps} {...sharedProps} />;
};

const filterExcludedOptions = (
  options: AdditionalFieldOption[],
  excludedOptions?: AdditionalFieldOption['type'][]
) => {
  if (!excludedOptions?.length) return options;
  return options.filter((option) => !excludedOptions.includes(option.type));
};

const filterSelectedOptions = (
  options: AdditionalFieldOption[],
  selectOptions?: AdditionalFieldOption['type'][]
) => {
  if (!selectOptions?.length) return options;
  return options.filter((option) => selectOptions.includes(option.type));
};

const getFilteredOptions = ({
  options,
  excludedOptions,
  selectOptions,
}: {
  options: AdditionalFieldOption[];
  excludedOptions?: AdditionalFieldOption['type'][];
  selectOptions?: AdditionalFieldOption['type'][];
}) => {
  const _options = filterExcludedOptions(options, excludedOptions);
  return filterSelectedOptions(_options, selectOptions);
};

const getItemValidator = (item: AdditionalFieldWithValue) => {
  if (item.type == 'email') {
    return isEmail('Invalid email');
  }
  if (item.type == 'date') {
    return (value) => (DateTime.fromJSDate(value).isValid ? undefined : 'Invalid date');
  }
  if (item.type == 'number' || item.type == 'dollar') {
    return (value) => (value && !isNaN(Number(value)) ? undefined : 'Invalid number');
  }
  if (item.type == 'checkbox' || item.type == 'boolean') {
    return (value) => (value ? undefined : 'This field is required');
  }

  return hasLength({ min: 1 }, 'This field is required');
};

const getOptionsItemValue = (
  item: AdditionalFieldOption,
  values?: Pick<AdditionalFieldsFormsProps, 'values'>
) => {
  // id is optional, when present we'd want to match it by keyor attemtpt with the name
  return values?.[item.id || item.name] ?? '';
};

const AdditionalFieldsForms = ({
  options,
  values,
  onChange,
  excludedOptions,
  selectOptions,
  title,
  titleProps,
  formId,
}: AdditionalFieldsFormsProps) => {
  const _options = useMemo(() => {
    if (!options) return [];
    const _options = getFilteredOptions({ options, excludedOptions, selectOptions });
    return _options.map((item) => ({
      value: getOptionsItemValue(item, values),
      ...item,
      key: randomId(),
    }));
  }, [excludedOptions, options, selectOptions, values]);

  const form = useForm<AdditionalFormFieldsForm, transformedValues>(
    {
      initialValues: {
        options: _options,
      },
      validate: (values) => {
        const errors = {};
        values.options.forEach((item, index) => {
          if (item.required) {
            const validator = getItemValidator(item);
            errors[`options.${index}.value`] = validator(item.value);
          }
        });

        return errors;
      },
      transformValues: (values) => ({
        options: formatValues(values.options),
      }),
      onValuesChange: (values) => {
        onChange?.(values.options, formatValues(values.options));
      },
    },
    { id: formId ?? 'additional_fields' }
  );

  // Sync form options only when the computed options or their values actually change
  useEffect(() => {
    const currentOptions = form.values?.options ?? [];
    const syncedOptions = _options.map((opt) => {
      const existing = currentOptions.find((curr) => curr.name === opt.name);
      return {
        ...opt,
        value: (existing?.value as string) ?? '',
      };
    });

    const hasDiff =
      currentOptions.length !== syncedOptions.length ||
      currentOptions.some(
        (curr, idx) =>
          curr.name !== syncedOptions[idx]?.name ||
          curr.value !== syncedOptions[idx]?.value ||
          !arraysEqual(curr.options ?? [], syncedOptions[idx]?.options ?? [])
      );

    if (hasDiff) {
      form.setValues({ options: syncedOptions });
    }
  }, [_options]);

  return (
    <Collapse in={form.values?.options?.length > 0}>
      <Stack gap="md" ref={form.formRef}>
        <SectionTitle title={title} titleProps={titleProps} />
        {form.values?.options?.map((option, index) => (
          <Field
            required={option.required}
            type={option.type}
            name={option.name}
            value={option.value}
            key={option.key}
            options={option.options}
            inputProps={form.getInputProps(`options.${index}.value`, {
              type: option.type === 'boolean' ? 'checkbox' : 'input',
            })}
          />
        ))}
      </Stack>
    </Collapse>
  );
};

const AdditionalFieldsFormsGuard = ({
  options,
  onChange,

  values,
  ...props
}: Partial<AdditionalFieldsFormsProps>) => {
  const hasOptions = useMemo(
    () =>
      options &&
      Boolean(
        getFilteredOptions({
          options,
          excludedOptions: props.excludedOptions,
          selectOptions: props.selectOptions,
        })
      ),
    [options, props.excludedOptions, props.selectOptions]
  );

  if (!hasOptions || !options?.length) return null;
  return <AdditionalFieldsForms options={options} onChange={onChange} {...props} values={values} />;
};

export { AdditionalFieldsForms, AdditionalFieldsFormsGuard };
export type { AdditionalFormFieldsForm, UseAdditionalFieldsForm };
